/**
 * TableAutoConvert — converts typed markdown table syntax into rendered TipTap table nodes.
 *
 * Works with ActiveBlockFlattener: when cursor leaves a paragraph that contains
 * a valid markdown table (typed with Shift+Enter between rows), this extension
 * replaces the paragraph with an actual table node.
 *
 * One-way conversion (v1): paragraph → table. Undo via Ctrl+Z restores the paragraph.
 */
import { Extension } from '@tiptap/react'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { Node as PMNode, Schema } from '@tiptap/pm/model'

const pluginKey = new PluginKey('tableAutoConvert')
const META_KEY = 'tac:skip'

// ---- Text extraction ----

function extractTextWithBreaks(node: PMNode): string {
  let text = ''
  node.content.forEach((child) => {
    if (child.type.name === 'hardBreak') {
      text += '\n'
    } else if (child.isText && child.text) {
      text += child.text
    }
  })
  return text
}

// ---- Table parsing helpers ----

function splitTableRow(line: string): string[] {
  const trimmed = line.trim()
  // Strip leading and trailing pipes
  const inner = trimmed.slice(1, -1)
  return inner.split('|').map((cell) => cell.trim())
}

function isSeparatorRow(line: string): boolean {
  const cells = splitTableRow(line)
  return cells.length > 0 && cells.every((cell) => /^[\s:]*-+[\s:]*$/.test(cell))
}

interface TableParseResult {
  hasHeader: boolean
  lines: string[]
}

function parseMarkdownTable(text: string): TableParseResult | null {
  const lines = text.split('\n').filter((l) => l.trim().length > 0)
  if (lines.length < 2) return null

  // All lines must start and end with |
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return null
  }

  // All rows must have the same column count
  const colCount = splitTableRow(lines[0]).length
  if (colCount === 0) return null
  for (const line of lines) {
    if (splitTableRow(line).length !== colCount) return null
  }

  // Check if line 2 is a separator (header table: header + separator + data)
  if (lines.length >= 3 && isSeparatorRow(lines[1])) {
    return { hasHeader: true, lines }
  }

  // No separator — headerless table (all data rows, no separators allowed)
  if (lines.some((l) => isSeparatorRow(l))) return null

  return { hasHeader: false, lines }
}

function makeCell(schema: Schema, cellText: string, isHeader: boolean): PMNode {
  const content = cellText
    ? schema.nodes.paragraph.create(null, schema.text(cellText))
    : schema.nodes.paragraph.create()
  const nodeType = isHeader ? schema.nodes.tableHeader : schema.nodes.tableCell
  return nodeType.create(null, content)
}

function buildTableNode(parsed: TableParseResult, schema: Schema): PMNode | null {
  const { hasHeader, lines } = parsed
  const tableRows: PMNode[] = []

  if (hasHeader) {
    // Header row
    const headerCells = splitTableRow(lines[0]).map((t) => makeCell(schema, t, true))
    tableRows.push(schema.nodes.tableRow.create(null, headerCells))
    // Data rows (skip separator at index 1)
    for (const line of lines.slice(2)) {
      const cells = splitTableRow(line).map((t) => makeCell(schema, t, false))
      tableRows.push(schema.nodes.tableRow.create(null, cells))
    }
  } else {
    // All rows are regular data cells
    for (const line of lines) {
      const cells = splitTableRow(line).map((t) => makeCell(schema, t, false))
      tableRows.push(schema.nodes.tableRow.create(null, cells))
    }
  }

  try {
    return schema.nodes.table.create(null, tableRows)
  } catch {
    return null
  }
}

// ---- Extension ----

export const TableAutoConvert = Extension.create({
  name: 'tableAutoConvert',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,

        appendTransaction(transactions, oldState, newState) {
          // Skip our own transactions
          if (transactions.some((t) => t.getMeta(META_KEY))) return null

          // Determine old and new cursor block positions
          const oldHead = oldState.selection.$head
          const newHead = newState.selection.$head

          let oldBlockPos: number | null = null
          if (oldHead.depth > 0) {
            oldBlockPos = oldHead.before(oldHead.depth)
          }

          let newBlockPos: number | null = null
          if (newHead.depth > 0) {
            newBlockPos = newHead.before(newHead.depth)
          }

          // Cursor must have moved to a different block
          if (oldBlockPos === null || oldBlockPos === newBlockPos) return null

          // Map old position through all transactions to find it in newState
          let mappedPos = oldBlockPos
          for (const t of transactions) {
            mappedPos = t.mapping.map(mappedPos)
          }

          const node = newState.doc.nodeAt(mappedPos)
          if (!node || node.type.name !== 'paragraph') return null

          const text = extractTextWithBreaks(node)
          const parsed = parseMarkdownTable(text)
          if (!parsed) return null

          const tableNode = buildTableNode(parsed, newState.schema)
          if (!tableNode) return null

          const tr = newState.tr
          try {
            tr.replaceWith(mappedPos, mappedPos + node.nodeSize, tableNode)
            tr.setMeta(META_KEY, true)
            return tr
          } catch {
            return null
          }
        },
      }),
    ]
  },
})
